import React, { useMemo, useState } from 'react';

import uniqBy from 'lodash/uniqBy';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { APP_EXCLUSIVE_TIMEZONE } from '~/consts/appExclusive';

import { useGetAppExclusiveSlotCheck } from '~/hooks/apis/offers/useGetAppExclusiveSlotCheck';
import { useMoveAppExclusiveSchedule } from '~/hooks/apis/offers/useMoveAppExclusiveSchedule';

import type { ScheduleRecord, TakenAppExclusive } from '~/services/OffersService';

import {
  formatDayLabel,
  formatLengthLabel,
  formatSlotTime,
  getFullSlotDays,
  getScheduleDate,
  getScheduleLengthDays,
  getScheduleStatus,
  getSlotBlockReason,
  todayDate,
} from './appOnlyFlashCalendar';

const DATE_FORMAT = 'YYYY-MM-DD';

interface Props {
  /** Row being moved; null while the dialog is closed. */
  schedule: ScheduleRecord | null;
  regionName: string;
  getOfferName: (offerId: string) => string | undefined;
  onClose: () => void;
}

export default function MoveAppOnlyFlashDealDialog({ schedule, regionName, getOfferName, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const open = schedule !== null;
  const fromDate = schedule ? getScheduleDate(schedule) : todayDate();
  // Derived on the same render the dialog opens, so the first slot check is for the default day.
  // Setting it in an effect fired one check for a stale day first, which was then aborted.
  const defaultDate = dayjs.utc(fromDate, DATE_FORMAT).add(1, 'day').format(DATE_FORMAT);
  // Tagged with the row it was picked for, so reopening on another deal starts from its default.
  const [picked, setPicked] = useState<{ scheduleId: number; date: string } | null>(null);
  const date = schedule?.id !== undefined && picked?.scheduleId === schedule.id ? picked.date : defaultDate;
  // Deal picked to swap with, tagged with the row and day it was picked for so a new day starts unpicked.
  const [swapPick, setSwapPick] = useState<{ scheduleId: number; date: string; otherId: number } | null>(null);

  const offerId = schedule?.fk_offer_salesforce_id ?? '';
  const region = schedule?.region ?? '';
  const offerName = getOfferName(offerId) ?? offerId;
  // The deal keeps its 24 or 48 hour length on its new day.
  const lengthDays = schedule ? getScheduleLengthDays(schedule) : 1;
  const isSameDay = date === fromDate;

  const targetCheck = useGetAppExclusiveSlotCheck(
    { region, date, days: lengthDays, offerId, offerName: getOfferName(offerId) },
    open && !isSameDay,
  );
  const eligible = targetCheck.data;

  const fullDays = useMemo(
    () => (eligible && schedule ? getFullSlotDays(eligible, [schedule.id]) : []),
    [eligible, schedule],
  );
  // Upcoming deals on the target slot's full days. Picking one swaps it onto this deal's old day.
  // Only one deal can be swapped, so a deal is offered only if moving it out frees every full day
  // (e.g. a 48 hour move onto two full days needs one 48 hour deal covering both).
  const swapCandidates = useMemo<Array<TakenAppExclusive>>(() => {
    if (!eligible || !schedule) {
      return [];
    }
    const onFullDays = uniqBy(
      fullDays.flatMap((day) => day.deals),
      'scheduleId',
    );
    return onFullDays.filter(
      (deal) =>
        getScheduleStatus(deal) === 'scheduled' &&
        getFullSlotDays(eligible, [schedule.id, deal.scheduleId]).length === 0,
    );
  }, [eligible, schedule, fullDays]);
  const isSwapPickCurrent = swapPick?.scheduleId === schedule?.id && swapPick?.date === date;
  const swapId = isSwapPickCurrent ? swapPick?.otherId : undefined;
  const taken = swapCandidates.find((deal) => deal.scheduleId === swapId) ?? null;
  const other: ScheduleRecord | null =
    taken && schedule && getScheduleStatus(taken) === 'scheduled'
      ? {
          ...schedule,
          id: taken.scheduleId,
          fk_offer_salesforce_id: taken.offerId,
          start: taken.start,
          end: taken.end,
        }
      : null;
  const otherName = other ? (getOfferName(other.fk_offer_salesforce_id) ?? other.fk_offer_salesforce_id) : '';

  const swapCheck = useGetAppExclusiveSlotCheck(
    {
      region,
      date: fromDate,
      days: other ? getScheduleLengthDays(other) : 1,
      offerId: other?.fk_offer_salesforce_id ?? '',
      offerName: other ? getOfferName(other.fk_offer_salesforce_id) : undefined,
    },
    open && !!other,
  );

  const ignore = [schedule?.id, other?.id].filter((id): id is number => id !== undefined);
  const moveReason = eligible && schedule ? getSlotBlockReason(eligible, offerId, ignore) : undefined;
  // The other deal is re-added after this one moves, so this deal's new slot counts on any day they share.
  const swapReason =
    other && swapCheck.data && eligible
      ? getSlotBlockReason(swapCheck.data, other.fk_offer_salesforce_id, ignore, undefined, [eligible.slot])
      : undefined;
  const isChecking = targetCheck.isFetching || (!!other && swapCheck.isFetching);
  const canSubmit =
    !!schedule &&
    !!eligible &&
    !isSameDay &&
    !isChecking &&
    !moveReason &&
    (!other || (!!swapCheck.data && !swapReason));

  const moveSchedule = useMoveAppExclusiveSchedule();

  const handleSubmit = async () => {
    if (!schedule || !eligible || !canSubmit) {
      return;
    }
    try {
      await moveSchedule.mutateAsync({
        schedule,
        searchName: getOfferName(offerId) ?? null,
        targetDate: date,
        swapWith: other
          ? {
              schedule: other,
              offerName: otherName,
              searchName: getOfferName(other.fk_offer_salesforce_id) ?? null,
              targetDate: fromDate,
            }
          : undefined,
      });
      enqueueSnackbar(
        other
          ? `Swapped ${offerName} (now ${formatDayLabel(date)}) with ${otherName} (now ${formatDayLabel(fromDate)})`
          : `${offerName} moved to ${formatDayLabel(date)}`,
        { variant: 'success' },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(`Could not move deal: ${err?.message ?? 'Unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="move-app-only-flash-deal-title">
      <DialogTitle id="move-app-only-flash-deal-title">Move deal · {regionName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            <strong>{offerName}</strong> ({formatLengthLabel(lengthDays)}) is on {formatDayLabel(fromDate)}. Pick the
            day it should run instead. If that day is full, pick an upcoming deal there to swap with.
          </Typography>
          <DatePicker
            label="New day"
            format="ddd D MMM YYYY"
            value={dayjs(date, DATE_FORMAT)}
            minDate={dayjs(todayDate(), DATE_FORMAT)}
            onChange={(value) =>
              schedule && value?.isValid() && setPicked({ scheduleId: schedule.id, date: value.format(DATE_FORMAT) })
            }
            slotProps={{ textField: { required: true } }}
          />

          {isSameDay && <Alert severity="info">That's the day it already runs on.</Alert>}
          {isChecking && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Checking the day...
              </Typography>
            </Stack>
          )}
          {targetCheck.isError && <Alert severity="error">Could not check the day: {targetCheck.error?.message}</Alert>}
          {!isChecking && eligible && !isSameDay && (
            <>
              <Typography variant="body2" color="text.secondary">
                Slot runs {formatSlotTime(eligible.slot.start)} to {formatSlotTime(eligible.slot.end)} (
                {APP_EXCLUSIVE_TIMEZONE}).
              </Typography>
              {moveReason && (
                <Alert severity="warning">
                  {offerName} can't run on {formatDayLabel(date)}: {moveReason}.
                </Alert>
              )}
              {fullDays.length > 0 && swapCandidates.length === 0 && (
                <Alert severity="info">
                  No deal there can be swapped: a swap moves one upcoming deal, and that has to free{' '}
                  {fullDays.map((day) => formatDayLabel(day.date)).join(' and ')}. Pick another day, or move one of
                  those deals first.
                </Alert>
              )}
              {swapCandidates.length > 0 && schedule && (
                <Stack spacing={0.5}>
                  <FormLabel id="move-app-only-flash-swap-label">Swap with</FormLabel>
                  <RadioGroup
                    aria-labelledby="move-app-only-flash-swap-label"
                    value={swapId ?? ''}
                    onChange={(event) =>
                      setSwapPick({ scheduleId: schedule.id, date, otherId: Number(event.target.value) })
                    }
                  >
                    {swapCandidates.map((deal) => (
                      <FormControlLabel
                        key={deal.scheduleId}
                        value={deal.scheduleId}
                        control={<Radio size="small" />}
                        label={`${getOfferName(deal.offerId) ?? deal.offerId} · ${formatLengthLabel(
                          getScheduleLengthDays(deal),
                        )} · ${formatSlotTime(deal.start)} to ${formatSlotTime(deal.end)}`}
                      />
                    ))}
                  </RadioGroup>
                </Stack>
              )}
              {!moveReason && other && swapReason && (
                <Alert severity="warning">
                  Can't swap: {otherName} can't run on {formatDayLabel(fromDate)}: {swapReason}.
                </Alert>
              )}
              {!moveReason && other && !swapReason && swapCheck.data && (
                // Full times, not just days: a 48 hour deal keeps its length, so it can leave or cover more
                // days than the one being swapped.
                <Alert severity="info">
                  {otherName} ({formatLengthLabel(getScheduleLengthDays(other))}) runs {formatSlotTime(other.start)} to{' '}
                  {formatSlotTime(other.end)}. It will move to {formatSlotTime(swapCheck.data.slot.start)} to{' '}
                  {formatSlotTime(swapCheck.data.slot.end)}.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || moveSchedule.isPending}>
          {moveSchedule.isPending ? 'Moving...' : other ? 'Swap deals' : 'Move deal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
